/**
 * Theme: Light / Dark / System.
 *
 * Owns the `data-theme` attribute on <html>, which every token in tokens.css keys
 * off. The initial value is applied by the inline script in `index.html` BEFORE
 * first paint (no flash) — this module must use the same storage key.
 */
import { createEffect, createRoot, createSignal, onCleanup } from "solid-js";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "rateslk.admin.theme";

function readStored(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    /* private mode / storage blocked */
  }
  return "system";
}

const prefersDark = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;

// Module-scope reactive state must own its root, otherwise Solid warns that these
// computations "will never be disposed". The theme genuinely lives for the whole
// page lifetime, so an explicit long-lived root is the correct expression of that.
const themeState = createRoot(() => {
  const [preference, setPreferenceSignal] = createSignal<ThemePreference>(readStored());
  const [systemDark, setSystemDark] = createSignal(prefersDark());
  return { preference, setPreferenceSignal, systemDark, setSystemDark };
});

const { preference, setPreferenceSignal, systemDark, setSystemDark } = themeState;

/** The user's preference (may be "system"). */
export const themePreference = preference;

/** What is actually on screen right now. */
export const resolvedTheme = (): ResolvedTheme =>
  preference() === "system" ? (systemDark() ? "dark" : "light") : (preference() as ResolvedTheme);

export function setThemePreference(next: ThemePreference): void {
  setPreferenceSignal(next);
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* nothing we can do; the in-memory preference still applies for this session */
  }
}

/**
 * Mount once at the app root. Keeps <html data-theme> in sync and follows the OS
 * while the preference is "system".
 */
export function useThemeEffect(): void {
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
  mq.addEventListener("change", onChange);
  onCleanup(() => mq.removeEventListener("change", onChange));

  createEffect(() => {
    document.documentElement.setAttribute("data-theme", resolvedTheme());
  });
}
